import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getFirestore, collection, query, where, onSnapshot, doc, updateDoc, arrayUnion, getDocs, Timestamp, documentId } from 'firebase/firestore';
import { useMusic } from '../../context/MusicContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { debounce } from 'lodash';
import toast from 'react-hot-toast';
import saveIcon from '../../assets/save.png';
import trendsSavedIcon from '../../assets/trends_saved.png';

const functions = getFunctions();
const searchAppContent = httpsCallable(functions, 'searchAppContent');
const db = getFirestore();

// 🌟 UPGRADED: Added isDarkMode checking to structural rows
const SongRow = ({ song, onAdd, isAdding, isAdded, isSponsored, isDarkMode }) => (
    <div className={`flex items-center p-2 rounded-xl relative border transition-all ${
        isDarkMode 
            ? "bg-zinc-950 border-zinc-800 text-white" 
            : "bg-gray-50 border-gray-100 text-gray-900"
    }`}>
        {isSponsored && (
            <span className={`absolute -top-1 -right-1 text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full z-10 border ${
                isDarkMode 
                    ? "bg-pink-950/40 border-pink-500/30 text-pink-400" 
                    : "bg-pink-100 border-pink-200 text-pink-700"
            }`}>
                Sponsored
            </span>
        )}
        <img 
            src={song.albumArtworkURL || song.imageUrl} 
            alt={song.title} 
            className="w-12 h-12 rounded-lg object-cover flex-shrink-0" 
        />
        <div className="flex-grow mx-3 min-w-0">
            <p className="font-bold text-xs uppercase tracking-tight truncate">{song.title}</p>
            <p className={`text-xs truncate font-medium ${isDarkMode ? "text-zinc-500" : "text-gray-500"}`}>
                {song.artistName || song.subtitle}
            </p>
        </div>
        <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
            {isAdded ? (
                <img src={trendsSavedIcon} alt="Added" className="w-6 h-6 invert-0 dark:brightness-125" />
            ) : isAdding ? (
                <RowSpinner />
            ) : (
                <button 
                    onClick={() => onAdd(song)} 
                    className={`p-2 rounded-full transition-colors focus:outline-none ${
                        isDarkMode ? "hover:bg-zinc-900" : "hover:bg-gray-200"
                    }`}
                >
                    <img src={saveIcon} alt="Add" className={`w-6 h-6 ${isDarkMode ? "invert opacity-80 hover:opacity-100" : ""}`} />
                </button>
            )}
        </div>
    </div>
);

const RowSpinner = () => (
    <div className="w-5 h-5 border-2 border-t-transparent border-brand-pink rounded-full animate-spin"></div>
);

// 🌟 UPGRADED: Accepting the explicit `isDarkMode` parameter
const AddSongToPlaylistModal = ({ isOpen, onClose, playlist, onSongAdded, isDarkMode }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false); // Make sure this is present if needed
  const [suggestedSongs, setSuggestedSongs] = useState([]); // 🌟 FIX: ADD THIS LINE BACK IN

  const [isSuggestionsLoading, setIsSuggestionsLoading] = useState(true);
  const [isSearchLoading, setIsSearchLoading] = useState(false);

  const [isAddingId, setIsAddingId] = useState(null);
  const [addedSongIds, setAddedSongIds] = useState(new Set());
  const [originalSongIds, setOriginalSongIds] = useState(new Set());

  
  const { trendsQueue, hydratedSongs } = useMusic();

  const performSearch = useCallback(
    debounce(async (term) => {
      if (term.length < 2) {
        setSearchResults([]);
        setIsSearchLoading(false);
        return;
      }
      setIsSearchLoading(true);
      try {
        const response = await searchAppContent({ searchTerm: term });
        const songResults = (response.data.results || [])
          .filter(r => r.type === 'song')
          
          .filter(song => song.isDeleted !== true); 
          
        setSearchResults(songResults);
      } catch (error) {
        console.error("Search failed:", error);
      } finally {
        setIsSearchLoading(false);
      }
    }, 500),
    []
  );

  useEffect(() => {
    if (searchTerm) {
        performSearch(searchTerm);
    } else {
        setSearchResults([]);
        setIsSearchLoading(false);
    }
  }, [searchTerm, performSearch]);

  const handleAddSong = async (song) => {
    if (isAddingId === song.id) return;
    setIsAddingId(song.id);
    try {
      const playlistRef = doc(db, 'playlists', playlist.id);
      await updateDoc(playlistRef, {
        songIds: arrayUnion(song.id)
      });
      setAddedSongIds(prev => new Set(prev).add(song.id));
    } catch (error) {
      toast.error("Failed to add song.");
    } finally {
      setIsAddingId(null);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    setOriginalSongIds(new Set(playlist.songIds));

    const fetchRecommendations = async () => {
      setIsSuggestionsLoading(true);
      try {
        const now = Timestamp.now();
        const featuresRef = collection(db, 'featuredSongs');
        const qFeatures = query(featuresRef, where('status', '==', 'active'), where('promotionEndDate', '>', now));
        const featuresSnap = await getDocs(qFeatures);
        const sponsoredSongIds = new Set(featuresSnap.docs.map(doc => doc.data().songId));
        
        const trendsSongs = trendsQueue
            .map(item => hydratedSongs[item.id])
            .filter(song => song && song.isDeleted !== true && !sponsoredSongIds.has(song.id) && !originalSongIds.has(song.id));
        
        const sponsoredSongDocs = [];
        if (sponsoredSongIds.size > 0) {
            const songsRef = collection(db, 'songs');
            const qSongs = query(songsRef, where(documentId(), 'in', Array.from(sponsoredSongIds)));
            const songsSnap = await getDocs(qSongs);
            songsSnap.forEach(doc => {
                if (!originalSongIds.has(doc.id)) {
                    const data = doc.data();
                    if (!originalSongIds.has(doc.id) && data.isDeleted !== true) {
                      sponsoredSongDocs.push({ 
                          ...data, 
                          id: doc.id, 
                          isSponsored: true,
                          albumArtworkURL: data.albumArtworkURL,
                          title: data.title,
                          artistName: data.artistName
                      });
                    }
                }
            });
        }
        
        const combined = [...sponsoredSongDocs, ...trendsSongs];
        combined.sort(() => 0.5 - Math.random());
        setSuggestedSongs(combined.slice(0, 10));
      } catch (error) {
        console.error("Error fetching recommendations:", error);
      } finally {
        setIsSuggestionsLoading(false);
      }
    };

    fetchRecommendations();
  }, [isOpen, db, trendsQueue, hydratedSongs, playlist.songIds]);

  const handleClose = () => {
    if (addedSongIds.size > 0 && onSongAdded) {
        onSongAdded();
    }
    setSearchTerm('');
    setSearchResults([]);
    setAddedSongIds(new Set());
    setOriginalSongIds(new Set());
    onClose(); 
  };

  if (!isOpen) return null;

  const songsToShow = searchTerm ? searchResults : suggestedSongs;
  const showLoading = searchTerm ? isSearchLoading : isSuggestionsLoading;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
      {/* 🌟 UPGRADED: Unified Light/Dark Modal Base Chassis */}
      <div className={`rounded-2xl shadow-2xl p-6 w-full max-w-md flex flex-col border transition-all duration-300 ${
        isDarkMode 
          ? "bg-zinc-900 border-zinc-800 text-white shadow-black/60" 
          : "bg-white border-slate-100 text-slate-900 shadow-slate-200"
      }`}>
        
        <h2 className="text-sm font-black uppercase tracking-wider mb-4 italic">
            {searchTerm ? "Search Pool" : "Suggested Tracks"}
        </h2>
        
        {/* 🌟 UPGRADED: Dynamic Search Input Deck */}
        <input 
          type="search" 
          placeholder="Search for a song..." 
          value={searchTerm} 
          onChange={(e) => setSearchTerm(e.target.value)} 
          className={`w-full p-3 text-xs font-semibold rounded-xl mb-4 border transition-all focus:outline-none focus:border-brand-pink/50 ${
            isDarkMode 
              ? "bg-zinc-950 border-zinc-800 text-white placeholder-zinc-600 shadow-[inset_0_1px_2px_rgba(0,0,0,0.4)]" 
              : "bg-white border-slate-200 text-slate-900 placeholder-slate-400"
          }`} 
        />
        
        {/* Scroll Containment Panel */}
        <div className="overflow-y-auto max-h-[45vh] space-y-2 pr-1 custom-scrollbar">
          {showLoading && (
            <p className={`text-center text-xs font-bold uppercase tracking-widest py-8 ${isDarkMode ? "text-zinc-600 animate-pulse" : "text-gray-400 animate-pulse"}`}>
                Querying Ledger...
            </p>
          )}
          
          {!showLoading && songsToShow.length === 0 && (
            <p className={`text-center text-xs font-semibold py-8 ${isDarkMode ? "text-zinc-500" : "text-gray-400"}`}>
                {searchTerm ? 'No tracks found matching criteria.' : 'No suggestions optimization context found.'}
            </p>
          )}

          {!showLoading && songsToShow.map((song) => {
            const isAdded = originalSongIds.has(song.id) || addedSongIds.has(song.id);
            const isCurrentlyAdding = isAddingId === song.id;

            return (
              <SongRow 
                key={song.id} 
                song={song} 
                onAdd={handleAddSong} 
                isAdding={isCurrentlyAdding}
                isAdded={isAdded}
                isSponsored={song.isSponsored || false} 
                isDarkMode={isDarkMode} // 🌟 Forward flag downstream
              />
            );
          })}
        </div>
        
        {/* Control Interactive Trigger Row */}
        <button 
          onClick={handleClose} 
          className={`mt-6 text-[10px] font-black uppercase tracking-widest self-center py-2 px-6 rounded-lg transition-colors focus:outline-none ${
            isDarkMode 
              ? "text-zinc-500 hover:text-white hover:bg-zinc-800/50" 
              : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
          }`}
        >
            Dismiss
        </button>
      </div>
    </div>
  );
};

export default AddSongToPlaylistModal;